import type { Document as ApiDocument } from "@/services/model";
import { log } from "@/utils/log";

type DownloadDocumentOptions = {
  /** Filename prefix for a single PDF, e.g. "DR_Rechnung"; a timestamp is appended. */
  pdfFilenamePrefix: string;
  /** Download name for a ZIP of several documents; without it a ZIP is rejected. */
  zipName?: string;
};

const clickAndCleanUp = (anchor: HTMLAnchorElement) => {
  document.body.appendChild(anchor);
  anchor.click();

  // cleanup (important for Safari)
  setTimeout(() => {
    window.URL.revokeObjectURL(anchor.href);
    anchor.remove();
  }, 100);
};

export const createPdfAnchor = (
  base64File: string,
  filenamePrefix: string,
): HTMLAnchorElement => {
  const anchor = document.createElement("a");
  const timestamp = new Date().toISOString().replaceAll(/:|\./g, "-");
  anchor.setAttribute("target", "_blank");
  anchor.setAttribute("download", `${filenamePrefix}-${timestamp}.pdf`);

  // On chrome, we cannot open data urls, so we convert to a blob and create it this way
  if (navigator.userAgent.includes("Firefox")) {
    anchor.setAttribute("href", `data:application/pdf;base64,${base64File}`);

    return anchor;
  }

  // decode base64 string, remove space for IE compatibility
  const binary = atob(base64File.replace(/\s/g, ""));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const blob = new Blob([bytes], { type: "application/pdf" });
  anchor.setAttribute("href", window.URL.createObjectURL(blob));

  return anchor;
};

/** Downloads a base64 PDF or ZIP from the API; logs anything else. */
export const downloadDocument = (
  apiDocument: ApiDocument | undefined,
  { pdfFilenamePrefix, zipName }: DownloadDocumentOptions,
) => {
  // The generated type says string[], the API sends one base64 string.
  const base64File = apiDocument?.file as unknown as string | undefined;
  const mimeType = apiDocument?.mimeType;

  if (base64File && mimeType === "application/pdf") {
    clickAndCleanUp(createPdfAnchor(base64File, pdfFilenamePrefix));

    return;
  }

  if (base64File && mimeType === "application/zip" && zipName) {
    const anchor = document.createElement("a");
    anchor.download = zipName;
    anchor.href = `data:application/zip;base64,${base64File}`;
    clickAndCleanUp(anchor);

    return;
  }

  log("Could not save document, unsupported or empty file:", mimeType);
};
